import { useState } from 'react';
import Spinner from '../../components/Spinner';
import { FOS_ACK_TEXT_VERSION } from '../../lib/fosApi';
import { fosError } from '../../lib/fosFormat';
import { ErrorNote } from './ui';

/**
 * The daily financial-access acknowledgment. The SERVER refuses the first financial read of each council-local day and delivers
 * nothing; a screen that gets that refusal (see isFinancialAckRequired) shows this dialog, records the acknowledgment, and then
 * repeats its request. One acknowledgment covers every donor and device for the day. Nothing delivers financial records yet; the
 * dialog is built and tested ahead of its first use.
 *
 * `acknowledge` is injected so the component is tested without a network.
 */
export const FINANCIAL_ACK_TITLE = 'Financial Information Access';
export const FINANCIAL_ACK_BODY = 'Individual donor financial information is confidential. Use it only to carry out your authorized Scouting responsibilities. Your access to this information will be logged.';
export const FINANCIAL_ACK_BUTTON = 'Understood';

export interface FinancialAckModalProps {
  acknowledge: (textVersion: string) => Promise<unknown>;
  onAcknowledged: () => void;
  onCancel: () => void;
}

/** The whole decision of the Understood button, kept pure so it is tested without a browser. */
export async function submitAcknowledgment(acknowledge: FinancialAckModalProps['acknowledge']): Promise<{ ok: true } | { ok: false; message: string }> {
  try { await acknowledge(FOS_ACK_TEXT_VERSION); return { ok: true }; } catch (cause) { return { ok: false, message: fosError(cause).message }; }
}

export default function FinancialAckModal({ acknowledge, onAcknowledged, onCancel }: FinancialAckModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const confirm = async () => {
    setBusy(true); setError('');
    const result = await submitAcknowledgment(acknowledge);
    if (result.ok) onAcknowledged(); else setError(result.message);
    setBusy(false);
  };
  return (
    <div className="fos-modal-backdrop" role="presentation">
      <div className="fos-modal" role="dialog" aria-modal="true" aria-labelledby="fos-ack-title" aria-describedby="fos-ack-body">
        <h2 id="fos-ack-title">{FINANCIAL_ACK_TITLE}</h2>
        <p id="fos-ack-body">{FINANCIAL_ACK_BODY}</p>
        <ErrorNote message={error} />
        <div className="fos-actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn primary" onClick={confirm} disabled={busy}>{busy ? <Spinner /> : FINANCIAL_ACK_BUTTON}</button>
        </div>
      </div>
    </div>
  );
}
